import * as yup from 'yup';
import { useForm } from 'react-form-state-manager';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GetProductQuery as GetProduct } from '__generated__/graphql';
import { ImageInput } from '../../../../common/Common/UploadButton';
import { TranslatableEditor, TranslatableInput } from '../../../Translations/Inputs';
import { getServerErrors } from '../../../../common/helpers';
import { translationsForAttribute } from '../../../Translations/helpers';
import MailEditorLayout from '../../../Common/MailEditorLayout';
import UI from '../../../../common/UI';
import useEditProductMutation from './useEditProductMutation';
import useNavigationPrompt from '../../../../common/useNavigationPrompt';
import useProject from '../../../useProject';
import useValidation from '../../../../common/useValidation';

type Product = GetProduct['product'];
type Event = GetProduct['event'];

const editProductRestrictionsSchema = yup.object().shape({
  title: yup.string().required(),
});

export interface EditProductInfoFormProps {
  product: Product;
  event: Event
}

const EditProductInfoForm = ({ product, event }: EditProductInfoFormProps) => {
  const { t } = useTranslation();
  const project = useProject();

  const getValues = (product: Product) => ({
    title: product.title,
    title_translations: translationsForAttribute(product.translations, 'title'),
    description: product.description,
    description_translations: translationsForAttribute(product.translations, 'description'),
    instructions: product.instructions,
    instructions_translations: translationsForAttribute(product.translations, 'instructions'),
    image: null as any,
    delete_image: false,
  });

  const form = useForm({
    values: getValues(product),
  });

  useNavigationPrompt(form.changed());

  // Re-initialize Tiptap editors after saving.
  const [formKey, setFormKey] = useState(0);

  const [editProduct, { error, loading }] = useEditProductMutation({
    input: {
      id: product.id,
      info: form.values,
    },
    onSuccess: (product) => {
      form.reset(getValues(product));
      setFormKey((key) => key + 1);
    },
  });

  const { valid, errors } = useValidation({
    schema: editProductRestrictionsSchema,
    values: form.values,
    externalErrors: getServerErrors(error, 'info'),
  });

  return (
    <UI.Form onSubmit={() => editProduct()} key={formKey}>
      <UI.FormGrid>
        <UI.ServerErrors error={error} />

        <UI.Div>
          <UI.Legend sc={{ block: true, mb: 1 }}>
            {t('basic_info')}
          </UI.Legend>
          <UI.InputDescription>
            {t('product_basic_info_description')}
          </UI.InputDescription>
        </UI.Div>

        <UI.InputGroup sc={{ valid: !errors.title, touched: form.touched.title }}>
          <UI.InputLabel htmlFor="ProductTitle">
            {t('product_title')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <TranslatableInput
            form={form}
            errors={errors}
            name="title"
            id="ProductTitle"
            defaultLocale={product.locale}
            title={t('product_title')}
            maxLength={50}
            placeholder={t('product_title_placeholder')}
          />
          <UI.ErrorMessages attribute={t('product_title')} errors={errors.title} />
        </UI.InputGroup>

        <UI.InputGroup sc={{ valid: !errors.image, touched: form.touched.image }}>
          <UI.InputLabel>
            {t('image')}
          </UI.InputLabel>
          <UI.InputDescription>
            {t('sellable_image_description')}
          </UI.InputDescription>
          <ImageInput
            value={form.values.image}
            deleteImage={form.values.delete_image}
            onChange={(file) => {
              form.set('image', file);
              form.set('delete_image', !file);
            }}
            previewImage={product.image?.url}
            imageAlt={product.title}
            width={300}
            ratio={2}
          />
          <UI.ErrorMessages attribute={t('image')} errors={errors.image} />
        </UI.InputGroup>

        <UI.InputGroup
          sc={{ valid: !errors.description, touched: form.touched.description }}
        >
          <UI.InputLabel id="ProductDescriptionLabel">
            {t('description')}
          </UI.InputLabel>
          <UI.InputDescription>
            {t('product_description_description')}
          </UI.InputDescription>
          <TranslatableEditor
            form={form}
            errors={errors}
            name="description"
            defaultLocale={product.locale}
            title={t('description')}
            attributes={{
              'aria-labelledby': 'ProductDescriptionLabel',
            }}
          />
          <UI.ErrorMessages attribute={t('description')} errors={errors.description} />
        </UI.InputGroup>

        <UI.InputGroup
          sc={{ valid: !errors.instructions, touched: form.touched.instructions }}
        >
          <UI.InputLabel id="ProductInstructionsLabel">
            {t('instructions')}
          </UI.InputLabel>
          <UI.InputDescription>
            {t('sellable_instructions_description')}
          </UI.InputDescription>
          <MailEditorLayout
            editor={({ maxContentWidth, ...props }) => (
              <TranslatableEditor
                form={form}
                errors={errors}
                name="instructions"
                defaultLocale={event.locale}
                title={t('instructions')}
                attributes={{
                  'aria-labelledby': 'ProductInstructionsLabel',
                }}
                {...props}
              />
            )}
            value={form.values.instructions}
            event={event}
            instructions={{
              title: form.values.title,
              instructions: form.values.instructions,
            }}
            showRegistrationSummary
            showOrderSummary
          />
          <UI.ErrorMessages attribute={t('instructions')} errors={errors.instructions} />
        </UI.InputGroup>

        <UI.FormButtons
          form={form}
          submitLabel={t('save_product')}
          closeTo={`/${project.id}/events/${event.id}/products`}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default EditProductInfoForm;
